import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { authActions, selectUser } from '~/thunks/auth/authSlice';
import { adminRoute } from '~/utils/routes/admin';
import { getAccountProfilePath } from '~/utils/routes/home';
import { getAppRole } from '~/utils/helper/role';
import ChangePasswordModal from '~/components/module/account/ChangePasswordModal';
import images from '~/assets/images';
import { IAccount } from '~/utils/interface/auth';

const getRoleLabel = (
  t: (key: string) => string,
  user?: { isSuperAdmin?: boolean; isStaff?: boolean; isSeniorAdmin?: boolean; isContact?: boolean }
) => {
  if (user?.isSuperAdmin) return t('role.superAdmin');
  if (user?.isStaff) return t('role.staff');
  if (user?.isSeniorAdmin) return t('role.seniorAdmin');
  if (user?.isContact) return t('role.contact');
  return t('role.admin');
};

const getDisplayName = (user?: IAccount & { firstName?: string; lastName?: string }) => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return fullName || `Account #${user?.id ?? '-'}`;
};

const Header = ({ onMenuClick }: { onMenuClick: () => void }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const { t } = useTranslation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(authActions.handleLogout());
    navigate(`${adminRoute.base}${adminRoute.login}`);
  };

  const profilePath = getAccountProfilePath(getAppRole(user));
  const avatarImage = (user as IAccount & { avatarImage?: string | null } | undefined)?.avatarImage;
  const roleLabel = getRoleLabel(t, user);
  const displayName = getDisplayName(user);

  return (
    <header id='header' className='flex h-14 shrink-0 items-center justify-between border-b border-border bg-card px-3 sm:px-4'>
      <div className='flex min-w-0 items-center gap-2'>
        <button
          id='mobile-menu-trigger'
          type='button'
          onClick={onMenuClick}
          className='inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-foreground transition hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring md:hidden'
          aria-label={t('nav.openMenu')}
          aria-haspopup='dialog'
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M4 6h16M4 12h16M4 18h16' />
          </svg>
        </button>
        <div className='aspect-[568/50] w-28 shrink-0 overflow-hidden sm:w-[170px]'>
        <img src={images.adminLogoSidebar} alt='SmartSeniors' className='h-full w-full object-contain' />
        </div>
      </div>

      <div ref={menuRef} className='relative'>
        <button
          onClick={() => setIsMenuOpen((open) => !open)}
          className='flex items-center gap-2 rounded-md px-2 py-1.5 transition hover:bg-muted'
        >
          <span className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-semibold text-primary-foreground'>
            {avatarImage ? <img src={avatarImage} alt='' className='h-full w-full object-cover' /> : roleLabel.slice(0, 2).toUpperCase()}
          </span>
          <span className='hidden flex-col items-start leading-tight sm:flex'>
            <span className='text-sm font-medium text-foreground'>{displayName}</span>
            <span className='text-xs text-muted-foreground'>{roleLabel}</span>
          </span>
          <svg
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            className={`shrink-0 text-muted-foreground transition-transform ${isMenuOpen ? 'rotate-180' : ''}`}
          >
            <path d='M6 9l6 6 6-6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </button>

        {isMenuOpen && (
          <div className='absolute right-0 top-12 w-56 rounded-md border border-border bg-card py-2 text-sm shadow-lg'>
            <button
              type='button'
              onClick={() => {
                setIsMenuOpen(false);
                navigate(profilePath);
              }}
              className='flex w-full items-center gap-2 border-b border-border px-3 pb-2 text-left transition hover:bg-muted'
              aria-label={t('header.viewProfile')}
            >
              <span className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-sm font-semibold text-primary-foreground'>
                {avatarImage ? <img src={avatarImage} alt='' className='h-full w-full object-cover' /> : roleLabel.slice(0, 2).toUpperCase()}
              </span>
              <div className='flex flex-col leading-tight'>
                <span className='font-medium text-foreground'>{displayName}</span>
                <span className='text-xs text-muted-foreground'>{roleLabel}</span>
              </div>
            </button>

            <button
              type='button'
              onClick={() => {
                setIsMenuOpen(false);
                setIsPasswordOpen(true);
              }}
              className='mt-1 flex w-full items-center gap-2 px-3 py-2 text-left text-foreground transition hover:bg-muted'
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
                <rect x='4' y='11' width='16' height='10' rx='2' stroke='currentColor' strokeWidth='2' />
                <path d='M8 11V7a4 4 0 018 0v4' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
              </svg>
              {t('header.changePassword')}
            </button>

            <button
              onClick={handleLogout}
              className='flex w-full items-center gap-2 px-3 py-2 text-left text-foreground transition hover:bg-muted'
            >
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              {t('header.logout')}
            </button>
          </div>
        )}
      </div>
      {isPasswordOpen && <ChangePasswordModal onClose={() => setIsPasswordOpen(false)} />}
    </header>
  );
};

export default Header;
